import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { restoreSession } from '@/redux/session-slice';
import { SignIn } from '@/screens/SignIn';
import { OrganizationPicker } from '@/screens/OrganizationPicker';
import { Shell } from '@/screens/Shell';
import { Overview } from '@/screens/Overview';
import { Connections } from '@/screens/Connections';
import { Rooms } from '@/screens/Rooms';
import { Mappings } from '@/screens/Mappings';
import { SyncHealth } from '@/screens/SyncHealth';
import { Directory } from '@/screens/Directory';
import { LoadingLine, Notice } from '@/components/ui';

/**
 * DEV ONLY, and this is what actually keeps it out of the production build.
 *
 * A STATIC import does not work here. The flag folds to `false` and
 * the route disappears, but the import statement still makes the module
 * reachable, so Rollup keeps it and the screen ships — verified by grepping the
 * built bundle, which is the only way to know. A dynamic `import()` inside a
 * statically-false branch is never evaluated and its chunk is never emitted.
 */
const Diagnostics = __DIAGNOSTICS_AVAILABLE__
  ? lazy(() => import('@/screens/Diagnostics').then((m) => ({ default: m.Diagnostics })))
  : null;

/**
 * Three gates, in order: signed in, an organisation chosen, then the app.
 *
 * Routing is client-side and Nest serves index.html for any non-API path, so a
 * refresh on a deep link lands back on the same screen rather than a 404.
 */
export function App() {
  const dispatch = useAppDispatch();
  const { status, organizationId, error } = useAppSelector((s) => s.session);

  useEffect(() => {
    if (status === 'unknown') {
      void dispatch(restoreSession());
    }
  }, [status, dispatch]);

  if (status === 'unknown') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        {/* The session restore is an API call like any other, so it says so
            rather than leaving a blank page with a sentence on it. */}
        <LoadingLine>Checking your sign-in…</LoadingLine>
      </div>
    );
  }

  if (status === 'signed-out') {
    return (
      <BrowserRouter>
        {error && (
          <div className="mx-auto w-full max-w-[520px] px-[20px] pt-[20px]">
            <Notice tone="broken" title="We could not reach ZenSpace">
              {error}
            </Notice>
          </div>
        )}
        <Routes>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="*" element={<SignIn />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (!organizationId) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<OrganizationPicker />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Overview />} />
          <Route path="/connections" element={<Connections />} />
          <Route path="/rooms" element={<Rooms />} />
          <Route path="/mappings" element={<Mappings />} />
          <Route path="/sync-health" element={<SyncHealth />} />
          <Route path="/directory" element={<Directory />} />
          {Diagnostics && (
            <Route
              path="/diagnostics"
              element={
                <Suspense fallback={<LoadingLine>Loading diagnostics…</LoadingLine>}>
                  <Diagnostics />
                </Suspense>
              }
            />
          )}
          {/* Anything else lands on the overview rather than a blank page. */}
          <Route path="/sign-in" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
